/**
 * Static copy for the parts of /events that aren't event cards — formats,
 * locations, beginner note, FAQ, external calendars. Same treatment as
 * orgasContent.js/infoFaq.js: real, honest copy, uncertain operational
 * specifics left as [prüfen] inline for the association to confirm rather
 * than invented as fact.
 */
export const EVENTS_CONTENT = {
  de: {
    planning: {
      title: "In Planung",
      intro:
        "Gerade ist kein Termin fest bestätigt — aber das hier ist wirklich in Planung. Wer zuerst Bescheid wissen will, trägt sich in den Newsletter ein; dort kündigen wir Termine an, bevor sie hier stehen.",
      newsletterCta: "Zum Newsletter",
      items: [
        "Realms of Lorell – Visit Silberquell · Frühjahr 2027",
        "Wayfarer – Exploring the East · 2027",
        "Realms of Lorell – Freund oder Feind · Herbst 2027",
      ],
    },
    formats: {
      title: "Unsere Formate",
      intro: "Nicht jedes LARP ist ein Wochenende im Wald. Wir spielen in drei Formaten — und jedes davon ist ein guter Einstieg.",
      items: [
        {
          title: "Tavernenabend",
          body: "Ein Abend in der Taverne unserer Spielwelt: Du kommst in Gewandung, setzt dich an den Tisch, und die Geschichte kommt zu dir — Händler, Gerüchte, ein Baron, der Geburtstag feiert. Kein Kampf, kein Regelwerk zum Auswendiglernen. Unsere Tavernenabende finden im Raum Dresden statt, meist an einem Samstagabend, 4–5 Stunden. Das ideale erste LARP.",
        },
        {
          title: "Fantasy-Wochenende (Con)",
          body: "Zwei bis drei Tage in unserer Spielwelt Realms of Lorell: Lagerleben, Plots, Intrigen, wenn es passt auch Kampf mit Polsterwaffen. Du spielst deine eigene Figur oder übernimmst als NSC eine Rolle aus der Geschichte. Übernachtung im Zelt oder in Gruppenunterkünften, Verpflegung vor Ort.",
        },
        {
          title: "Jugend-LARP",
          body: "Live-Rollenspiel für Jugendliche im Alter von 8–18 — als Tagesevent, Ferienangebot oder in Kooperation mit Jugendhäusern und Schulen. Mit Betreuungsschlüssel, geschulten Teamern und unserem Jugendschutzkonzept.",
          link: { textKey: "jugendarbeit", to: "jugendarbeit" },
        },
      ],
    },
    locations: {
      title: "Wo wir spielen",
      body:
        "Unsere Heimat ist Dresden — gespielt wird aber in ganz Sachsen, überall dort, wo ein Ort zur Geschichte passt. Tavernenabende haben wir bisher im Rittergut Leppersdorf (Wachau, rund 20 Minuten von Dresden) gespielt; für Wochenenden suchen wir Gelände mit Lagerplatz und Wald, gern auch im Umland oder in der Sächsischen Schweiz. Anreise aus Leipzig, Chemnitz oder Berlin: 1–2 Stunden — und viele fahren gemeinsam.",
      communitySuffix: "nach einer Mitfahrgelegenheit.",
      communityPrefix: "Frag in unserer",
    },
    beginners: {
      title: "Für Einsteiger",
      body:
        "Du hast noch nie ein LARP besucht? Dann ist ein Tavernenabend genau richtig. Du brauchst keine Ausrüstung, keine Regelkenntnis und niemanden, den du schon kennst — sag uns bei der Anmeldung, dass du neu bist, und wir kümmern uns.",
      links: [
        { textKey: "wasIstLarp", label: "Was ist LARP?" },
        { textKey: "faq", label: "unserer FAQ" },
      ],
    },
    faq: {
      title: "Häufige Fragen zu unseren Events",
      items: [
        {
          q: "Wie melde ich mich für ein Event an?",
          a: "Über den Button auf der Event-Seite. Du bekommst eine Bestätigung per Mail; bezahlt wird per Überweisung oder PayPal.",
        },
        {
          q: "Was kostet ein Event bei Tales&Legends?",
          a: "Das lässt sich pauschal nicht sagen — die Kosten sind von Event zu Event sehr unterschiedlich, von einem günstigen Tavernenabend bis zu einem mehrtägigen Wochenende. Den genauen Preis findest du immer auf der jeweiligen Event-Seite. Wer sich das nicht leisten kann, fragt nach einem Freiplatz — diskret über das Orga-Team.",
        },
        {
          q: "Kann ich als Anfänger mitmachen?",
          a: "Ja. Als Organisatoren des Anfänger-Camps Wayfarer auf dem Conquest of Mythodea wissen wir genau, wie man mit Anfängern umgeht, und helfen dir so gut es geht ins Spiel.",
        },
        {
          q: "Ab welchem Alter kann ich teilnehmen?",
          a: "Das ist von Event zu Event unterschiedlich — schau dir dazu die Altersangabe auf der jeweiligen Event-Seite an. Einige Events sind erst ab 18, für andere reicht es, wenn dich ein Erziehungsberechtigter begleitet, und bei Jugend-LARP-Veranstaltungen gelten wieder eigene Regeln.",
        },
        {
          q: "Wo finde ich weitere LARP-Termine in Deutschland?",
          a: "Die vollständigste Übersicht bietet larpkalender.de, viele Veranstalter tragen ihre Events auch bei larp-radar.com ein. Unsere eigenen Termine findest du zusätzlich dort — und immer hier auf unserer Eventseite.",
          links: [
            { label: "larpkalender.de", href: "https://www.larpkalender.de" },
            { label: "larp-radar.com", href: "https://www.larp-radar.com" },
          ],
        },
      ],
    },
  },
  en: {
    planning: {
      title: "In the Works",
      intro:
        "No date is confirmed right now — but these are genuinely in the works. Sign up for the newsletter to be the first to know; we announce dates there before they show up here.",
      newsletterCta: "Sign up for the newsletter",
      items: [
        "Realms of Lorell – Visit Silberquell · Spring 2027",
        "Wayfarer – Exploring the East · 2027",
        "Realms of Lorell – Friend or Foe · Autumn 2027",
      ],
    },
    formats: {
      title: "Our Formats",
      intro: "Not every LARP is a weekend in the woods. We run three formats — and each one is a good way in.",
      items: [
        {
          title: "Tavern night",
          body: "An evening in our world's tavern: you show up in costume, take a seat, and the story comes to you — merchants, rumours, a baron celebrating his birthday. No combat, no rulebook to memorize. Our tavern nights run in the Dresden area, usually on a Saturday evening, 4–5 hours. The ideal first LARP.",
        },
        {
          title: "Fantasy weekend (con)",
          body: "Two to three days in our world Realms of Lorell: camp life, plots, intrigue, and — where it fits — combat with foam weapons. Play your own character or step in as an NPC for a role from the story. Sleep in a tent or shared accommodation, food provided on site.",
        },
        {
          title: "Youth LARP",
          body: "Live-action roleplay for young people aged 8–18 — as a day event, a holiday programme, or in cooperation with youth centres and schools. With a staffing ratio, trained supervisors, and our youth protection concept.",
          link: { textKey: "jugendarbeit", to: "jugendarbeit" },
        },
      ],
    },
    locations: {
      title: "Where We Play",
      body:
        "Our home is Dresden — but we play across Saxony, wherever a location fits the story. We've run tavern nights at Rittergut Leppersdorf (Wachau, about 20 minutes from Dresden) so far; for weekends we look for grounds with camping space and forest, often further out or in Saxon Switzerland. Travelling from Leipzig, Chemnitz, or Berlin: 1–2 hours — and many people carpool.",
      communityPrefix: "Ask in our",
      communitySuffix: "if you're looking for a ride.",
    },
    beginners: {
      title: "For Beginners",
      body:
        "Never been to a LARP before? A tavern night is exactly right. You don't need any gear, rules knowledge, or anyone you already know — tell us when you sign up that you're new, and we'll take care of the rest.",
      links: [
        { textKey: "wasIstLarp", label: "What is LARP?" },
        { textKey: "faq", label: "our FAQ" },
      ],
    },
    faq: {
      title: "Frequently Asked Questions About Our Events",
      items: [
        {
          q: "How do I register for an event?",
          a: "Via the button on the event page. You'll get a confirmation by email; payment is by bank transfer or PayPal.",
        },
        {
          q: "What does an event at Tales&Legends cost?",
          a: "There's no simple answer — costs vary a lot from event to event, from an affordable tavern night to a multi-day weekend. You'll always find the exact price on the event's own page. If you can't afford it, ask about a free ticket — discreetly, via the organiser team.",
        },
        {
          q: "Can I join as a beginner?",
          a: "Yes. As the organisers of the Wayfarer beginners' camp at Conquest of Mythodea, we know exactly how to look after newcomers and will help you settle into the game as much as we can.",
        },
        {
          q: "What age do I need to be to take part?",
          a: "It varies from event to event — check the age info on the individual event page. Some events are 18+, others just need a parent or guardian with you, and youth LARP events have their own rules again.",
        },
        {
          q: "Where can I find more LARP dates in Germany?",
          a: "The most complete overview is larpkalender.de (German-language); many organisers also list their events on larp-radar.com. You'll find our own dates there too — and always here on our events page.",
          links: [
            { label: "larpkalender.de", href: "https://www.larpkalender.de" },
            { label: "larp-radar.com", href: "https://www.larp-radar.com" },
          ],
        },
      ],
    },
  },
};
